import React from "react";
import { Box, LinearProgress, Tooltip } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import { theme } from "../../../theme";

export interface StepData {
  label: string;
  status: "empty" | "circle" | "dot" | "tick" | "cross";
  on_hold?: boolean; // ✅ new property
}

interface ProgressStepperProps {
  steps: StepData[];
  currentStep: string | null; // text to show only on the active step
}

const ProgressStepper: React.FC<ProgressStepperProps> = ({ steps, currentStep }) => {
  const circleSize = 1.2; // vw
  const gap = 1.5; // vw

  const circlesContainerWidth =
    steps.length * circleSize + (steps.length - 1) * gap;

  const renderCircle = (status: StepData["status"], on_hold?: boolean) => {
    // ✅ If candidate is on hold → override circle look
    if (on_hold) {
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            backgroundColor: "#FEDF89", // orange for OnHold
            border: "2px solid #FEDF89",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PauseCircleOutlineIcon
            sx={{ color: "white", fontSize: "1vw", fontWeight: "bold" }}
          />
        </Box>
      );
    }

    switch (status) {
      case "tick":
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              backgroundColor: theme.palette.primary.main,
              border: `2px solid ${theme.palette.primary.main}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckIcon
              sx={{ color: "white", fontSize: "0.8vw", fontWeight: "bold" }}
            />
          </Box>
        );

      case "cross":
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              backgroundColor: "#FF4D4F",
              border: "2px solid #FF4D4F",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="span"
              sx={{
                color: "white",
                fontSize: "0.8vw",
                fontWeight: "bold",
                lineHeight: 1,
                cursor: "default",
              }}
            >
              ✕
            </Box>
          </Box>
        );

      case "circle":
      case "dot":
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              border: `2px solid ${theme.palette.primary.main}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: "0.4vw",
                height: "0.4vw",
                bgcolor: theme.palette.primary.main,
                borderRadius: "50%",
              }}
            />
          </Box>
        );

      default: // empty
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
            }}
          />
        );
    }
  };

  // Find the active index — the first circle, dot, or cross (or on_hold)
  // Find the last active index
  const activeIndex = [...steps]
    .map((step, idx) =>
      step.status === "circle" ||
        step.status === "dot" ||
        step.status === "cross" ||
        step.on_hold ||
        step.status === "tick"  // ✅ include tick as active
        ? idx
        : -1
    )
    .filter((idx) => idx !== -1)
    .pop() ?? 0;

  const progressBarWidth =
    activeIndex === 0
      ? 0
      : activeIndex * (gap + circleSize) - gap / 2;



  return (
    <Box
      position="relative"
      sx={{
        padding: "2vw",
        width: `${circlesContainerWidth}vw`,
        pl: 0,
        py: 0,
      }}
    >
      {/* Progress bar */}
      <Box
        position="relative"
        top={`${circleSize / 1.8}vw`}
        left={`${circleSize / 2}vw`}
        sx={{ width: `${circlesContainerWidth - circleSize}vw`, zIndex: 0 }}
      >
        <LinearProgress
          variant="determinate"
          value={100}
          sx={{
            height: "0.15vw",
            backgroundColor: "grey.300",
            "& .MuiLinearProgress-bar": {
              backgroundColor: "grey.300",
            },
          }}
        />
      </Box>
      <Box
        position="absolute"
        top={`${circleSize / 1.8}vw`}
        left={`${circleSize / 2}vw`}
        sx={{ width: `${progressBarWidth}vw`, zIndex: 1 }}
      >
        <LinearProgress
          variant="determinate"
          value={100}
          sx={{
            height: "0.15vw",
            backgroundColor: "transparent",
            "& .MuiLinearProgress-bar": {
              backgroundColor: theme.palette.primary.main,
            },
          }}
        />
      </Box>

      {/* Circles */}
      <Box display="flex" alignItems="center" gap={`${gap}vw`}>
        {steps.map((step, index) => {
          const tooltipTitle =
            index === activeIndex && currentStep
              ? step.on_hold
                ? `${step.label} (On Hold)`
                : currentStep
              : step.label;
          return (
            <Box
              key={index}
              display="flex"
              flexDirection="column"
              alignItems="center"
              sx={{ zIndex: 2 }}
            >
              <Tooltip
                title={tooltipTitle}
                slotProps={{
                  popper: {
                    sx: {
                      '& .MuiTooltip-tooltip': {
                        bgcolor: 'white',
                        color: 'black',
                        fontWeight: 500,
                        fontSize: '0.95em',
                        boxShadow: 2,
                      }
                    }
                  }
                }}
              >
                <Box
                  sx={{
                    width: `${circleSize}vw`,
                    height: `${circleSize}vw`,
                  }}
                >
                  {renderCircle(step.status, step.on_hold)}
                </Box>
              </Tooltip>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ProgressStepper;
